import { Component, Inject, OnInit } from '@angular/core';
import { Procedure } from '../../../../model/procedure';
import { ProcedureService } from '../../../../services/procedure.service';
import { Subject, takeUntil } from 'rxjs';
import { ActionConfirmPasswordComponent } from '../../../shared/action-confirm-password/action-confirm-password.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from '../../../../services/spinner.service';
import { SnackBarService } from '../../../../services/snackBar.service';
import { AppType } from '../../../../model/appType';
import { AppTypeService } from '../../../../services/appType.service';
import { StringUtils } from '../../../../utils/stringUtils';
import { ArrayUtils } from '../../../../utils/arrayUtils';

export interface DialogData {
  procedure: Procedure;
  isEdit: boolean;
}
@Component({
  selector: 'app-procedure-edit',
  templateUrl: './procedure-edit-dialog.component.html',
  styleUrls: ['./procedure-edit-dialog.component.scss'],
  standalone: false,
})
export class ProcedureEditDialogComponent implements OnInit {

  procedures: Procedure[] = [];
  appTypes: AppType[];

  private destroy$ = new Subject<void>();

  constructor(private procedureService: ProcedureService,
    private appTypeService: AppTypeService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private spinnerService: SpinnerService,
    private snackBarService: SnackBarService,
    public dialogRef: MatDialogRef<ProcedureEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData) { }

  ngOnInit(): void {
    this.spinnerService.show();
    this.procedureService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: Procedure[]) => {
      this.procedures = data;

      this.appTypeService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: AppType[]) => {
        this.appTypes = data
        this.spinnerService.hide();
      }, error => {
        console.log(error);
        this.spinnerService.show();
      });
    }, error => {
      console.log(error);
      this.spinnerService.show();
    });
  }

  save(): void {
    const errors = this.validateForm();
    if (errors.length > 0) {
      this.snackBarService.sendError(errors.join('\n'));
      return;
    }

    const dialogRef = this.dialog.open(ActionConfirmPasswordComponent, {
      minWidth: '20%',
      maxHeight: '95vh',
      data: {}
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((token: string) => {
      if (token == null) return;

      this.spinnerService.show();
      this.doSave();
    });
  }

  private validateForm(): string[] {
    const errors = [];

    if (StringUtils.isEmpty(this.data.procedure.name)) {
      errors.push(this.translate.instant('procedureEdit.dialog.name.error'));
    }
    if (ArrayUtils.isEmpty(this.data.procedure.idApps)) {
      errors.push(this.translate.instant('procedureEdit.dialog.app.error'));
    }
    if (StringUtils.isEmpty(this.data.procedure.description)) {
      errors.push(this.translate.instant('procedureEdit.dialog.description.error'));
    }

    return errors;
  }

  private doSave(): void {
    const saveProcedure = () => {
      this.procedureService.save(this.data.procedure).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (item: Procedure) => {
            this.snackBarService.sendSuccess(this.translate.instant('procedureEdit.form.create.ok') as string);
            this.dialogRef.close(item);
          },
          error: () => {
            this.snackBarService.sendError(this.translate.instant('procedureEdit.form.create.error') as string);
            this.spinnerService.hide();
          }
        });
    };

    if (this.data.isEdit) {
      saveProcedure();
      return;
    }

    this.procedureService.existsByName(this.data.procedure.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (exist: boolean) => {
          if (exist) {
            this.snackBarService.sendError(this.translate.instant('procedureEdit.form.exists.error') as string);
            this.spinnerService.hide();
            return;
          }
          saveProcedure();
        },
        error: () => {
          this.snackBarService.sendError(this.translate.instant('procedureEdit.form.create.error') as string);
          this.spinnerService.hide();
        }
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
